/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Private route
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.0.0
 */

import { createElement, memo, useEffect } from 'react';

import { connect, useDispatch } from 'react-redux';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { pushGTMAnalytic } from '../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { addBreadCrumbs, clearBreadCrumbs } from '../modules/breadCrumbs/breadCrumbsActions';
import { EBreadCrumbType, IBreadCrumb } from '../modules/breadCrumbs/breadCrumbsTypes';
import { formBreadCrumbData } from '../modules/breadCrumbs/utils/formBreadCrumbData';
import { makeSelectTranslationCommon } from '../modules/internationalization/intSelectors';
import { ICommonTranslation, ICorporateTranslation } from '../modules/internationalization/types';
import { AccessComponentKeyEnum } from '../modules/role/constants/guard/guardConstants';
import Guard from '../modules/role/Guard';
import { guardComponentType } from '../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../modules/role/selectors/guardSelector';
import { makeSelectLoggedIn } from '../pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '../reducers/rootReducer';
import { IRouteParams } from './constants/routesConstants';

export interface IAppRouteProps extends RouteProps {
    accessKey: AccessComponentKeyEnum;
    breadCrumbs?: EBreadCrumbType[];
    breadCrumbsDisabled?: boolean;
    component: any;
    componentProps?: any;
    computedMatch?: {
        params: IRouteParams;
    };
    wrapper?: any;
    wrapperProps?: any;
}

type PrivateRouteProps = IAppRouteProps & IConnectedProps & IDispatchProps;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRender = (component: any, componentProps: any, wrapper: any, wrapperProps: any) => {
    const element = createElement(component, componentProps);
    if (!wrapper) {
        return element;
    }
    return createElement(wrapper, wrapperProps, element);
};

export const PrivateRoute = ({
                                 addBreadCrumbs,
                                 features,
                                 breadCrumbs,
                                 breadCrumbsDisabled,
                                 clearBreadCrumbs,
                                 component,
                                 componentProps,
                                 loggedIn,
                                 pushGtmAnalytic,
                                 translation,
                                 wrapper,
                                 wrapperProps,
                                 ...rest
                             }: PrivateRouteProps) => {
    let breadCrumbsData: IBreadCrumb[];

    const dispatch = useDispatch();

    useEffect(() => {
        if (breadCrumbsDisabled) return;

        if (breadCrumbs) {
            breadCrumbsData = formBreadCrumbData({
                breadCrumbs,
                dispatch,
                features,
                matchedClientId: rest.computedMatch?.params.clientId,
                matchedSubscriberId: rest.computedMatch?.params.subscriberId,
                matchedOrderId: rest.computedMatch?.params.orderId,
                matchedInquiryId: rest.computedMatch?.params.inquiryId,
                pagesTranslation: translation.pages,
            });
            addBreadCrumbs(breadCrumbsData);
        } else {
            clearBreadCrumbs();
        }
    }, [breadCrumbs, breadCrumbsDisabled, features, rest.location?.pathname]);

    useEffect(() => {
        pushGtmAnalytic({
            operation: GTMOperations.pageView.onPageView(),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [rest.location?.pathname]);

    return (
        <Guard accessKey={rest.accessKey}>
            <Route {...rest} render={(props: RouteComponentProps<any>) => (
                loggedIn
                    ? getRender(component, { ...componentProps, ...props }, wrapper, {
                        // wrapper props can overwrite default
                        match: props.match,
                        ...wrapperProps,
                    })
                    : <Redirect to="/login"/>
            )}/>
        </Guard>
    );
};

interface IConnectedProps {
    features: guardComponentType;
    loggedIn: boolean;
    translation: ICommonTranslation & ICorporateTranslation;
}

interface IDispatchProps {
    addBreadCrumbs: (payload: IBreadCrumb[]) => void;
    clearBreadCrumbs: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    features: makeSelectGuardComponents(),
    loggedIn: makeSelectLoggedIn(),
    translation: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    addBreadCrumbs: (payload: IBreadCrumb[]) => dispatch(addBreadCrumbs(payload)),
    clearBreadCrumbs: () => dispatch(clearBreadCrumbs()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(PrivateRoute));
